import type { ReactNode } from 'react';
import { Caption } from './primitives';
import { MODULE_LABEL_FR } from './labels';

/**
 * Panels partagés entre Mission Control et Parent Dashboard.
 *
 *  - CadencePanel — W/D/L bar par cadence + ELO delta (consomme
 *    `GET /games?cadence=...` agrégé via `GameRepository::cadenceSummaryForUser`).
 *  - SessionsPanel — liste cliquable des dernières sessions (consomme
 *    `GET /training/sessions?limit=N`).
 *  - ELOCurveCard — multi-line chart FIDE Std/Rapid + targets M6/M12 (consomme
 *    `GET /users/me/elo-history`).
 *
 * Port depuis `docs/prototypes/ui-kit-v2/ui_kits/the_chess_coach/Components.jsx`
 * §CadencePanel / §SessionsPanel / §ELOCurveCard. Tailwind utility où c'est
 * lisible, inline style pour les calculs SVG.
 */

const MODULE_COLORS: Record<string, string> = {
  opening: '#7B5BFF',
  tactic: '#3DD8FF',
  tactics: '#3DD8FF',
  endgame: '#FFB546',
  autopsy: '#FF5C7A',
};

const CADENCE_LABEL: Record<string, string> = {
  bullet: 'Bullet',
  blitz: 'Blitz',
  rapid: 'Rapid',
  classical: 'Classique',
  correspondence: 'Correspondance',
};

/* ─── CadencePanel ──────────────────────────────────────────────────────── */

export interface CadenceRow {
  id: string;
  w: number;
  d: number;
  l: number;
  games: number;
  rating_delta: number;
}

export function CadencePanel({
  cadence,
  totalGames,
  className = '',
}: {
  cadence: ReadonlyArray<CadenceRow>;
  totalGames: number;
  className?: string;
}): JSX.Element {
  // Tri descendant par nombre de parties → la cadence dominante en premier.
  const sorted = [...cadence].sort((a, b) => b.games - a.games);
  return (
    <div className={`bg-ink-800 border border-ink-700 rounded-xl p-5 ${className}`}>
      <div className="flex justify-between items-baseline mb-4">
        <Caption>Performance par cadence</Caption>
        <span className="font-mono text-[11px] text-ink-300 tabular-nums">
          {totalGames} parties
        </span>
      </div>
      <div className="flex flex-col gap-3">
        {sorted.length === 0 && (
          <p className="text-sm text-ink-300 italic">
            Aucune partie importée. Lance un import Lichess depuis Game Autopsy.
          </p>
        )}
        {sorted.map((c) => {
          const winRate = c.games > 0 ? (c.w / c.games) * 100 : 0;
          const drawRate = c.games > 0 ? (c.d / c.games) * 100 : 0;
          const lossRate = c.games > 0 ? (c.l / c.games) * 100 : 0;
          const deltaColor =
            c.rating_delta > 0 ? 'text-volt-500' : c.rating_delta < 0 ? 'text-coral-500' : 'text-ink-300';
          return (
            <div key={c.id}>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-sm text-paper-50">{CADENCE_LABEL[c.id] ?? c.id}</span>
                <div className="flex items-center gap-2.5 font-mono text-[11px] tabular-nums">
                  <span className="text-volt-500">{c.w}V</span>
                  <span className="text-ink-300">{c.d}N</span>
                  <span className="text-coral-500">{c.l}D</span>
                  <span className="text-ink-700">·</span>
                  <span className={`${deltaColor} min-w-[44px] text-right`}>
                    {c.rating_delta > 0 ? '+' : ''}
                    {c.rating_delta} ELO
                  </span>
                </div>
              </div>
              <div className="flex h-1.5 rounded-full overflow-hidden bg-ink-700">
                <div className="bg-volt-500" style={{ width: `${winRate}%` }} />
                <div className="bg-ink-300" style={{ width: `${drawRate}%` }} />
                <div className="bg-coral-500" style={{ width: `${lossRate}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── SessionsPanel ─────────────────────────────────────────────────────── */

export interface SessionRow {
  id: string;
  date: string;
  /** Format HH:MM */
  start_time: string;
  duration_min: number;
  attempts: number;
  success: number;
  modules: ReadonlyArray<string>;
  avg_sec: number;
}

export function SessionsPanel({
  sessions,
  onSelect,
  limit,
  className = '',
}: {
  sessions: ReadonlyArray<SessionRow>;
  onSelect?: (s: SessionRow) => void;
  limit?: number;
  className?: string;
}): JSX.Element {
  const visible = limit !== undefined ? sessions.slice(0, limit) : sessions;
  return (
    <div className={`bg-ink-800 border border-ink-700 rounded-xl p-5 ${className}`}>
      <div className="flex justify-between items-baseline mb-3">
        <Caption>Sessions récentes</Caption>
        <span className="font-mono text-[11px] text-ink-300 tabular-nums">
          {visible.length} dernières
        </span>
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-ink-300 italic">
          Aucune session enregistrée. Drille une position dans Tactics Dojo ou Opening Lab.
        </p>
      ) : (
        <ul className="flex flex-col gap-1 m-0 p-0 list-none">
          {visible.map((s) => {
            const rate = s.attempts > 0 ? Math.round((s.success / s.attempts) * 100) : 0;
            const rateColor = rate >= 70 ? 'bg-volt-500' : 'bg-amber-500';
            return (
              <li
                key={s.id}
                onClick={onSelect ? () => onSelect(s) : undefined}
                className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors duration-150 ${
                  onSelect ? 'cursor-pointer hover:bg-paper-50/5' : ''
                }`}
              >
                <span className="font-mono text-[11px] text-ink-300 tabular-nums w-14 flex-none">{s.date}</span>
                <span className="font-mono text-[11px] text-paper-50 tabular-nums w-10 flex-none">
                  {s.duration_min}m
                </span>
                <span className="font-mono text-[11px] tabular-nums whitespace-nowrap">
                  <span className="text-volt-500">{s.success}</span>
                  <span className="text-ink-300">/</span>
                  <span className="text-paper-50">{s.attempts}</span>
                </span>
                <div className="flex-1 h-1 bg-ink-700 rounded-full overflow-hidden">
                  <div className={`h-full ${rateColor}`} style={{ width: `${rate}%` }} />
                </div>
                <div className="flex gap-0.5">
                  {s.modules.map((m) => (
                    <span
                      key={m}
                      className="size-1.5 rounded-full"
                      style={{ background: MODULE_COLORS[m] ?? 'var(--ink-300)' }}
                      aria-label={MODULE_LABEL_FR[m] ?? m}
                    />
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ─── ELOCurveCard ──────────────────────────────────────────────────────── */

export interface EloPoint {
  v: number;
  label: string;
  /** ISO date YYYY-MM-DD, utile pour tooltips précis. */
  recorded_at?: string;
}

export function ELOCurveCard({
  standard = [],
  rapid = [],
  title = 'Trajectoire FIDE',
  height = 220,
  milestones = true,
  emptyHint,
}: {
  standard?: ReadonlyArray<EloPoint>;
  rapid?: ReadonlyArray<EloPoint>;
  title?: string;
  height?: number;
  milestones?: boolean;
  emptyHint?: ReactNode;
}): JSX.Element {
  const W = 600;
  const H = height;
  const P = 26;
  const allValues = [...standard.map((p) => p.v), ...rapid.map((p) => p.v)];
  const empty = allValues.length === 0;
  // Fallback range si vide pour ne pas casser le SVG.
  const min = empty ? 1600 : Math.min(...allValues, 1600) - 30;
  const max = empty ? 2200 : Math.max(...allValues, 2050) + 80;
  const nx = Math.max(standard.length, rapid.length) - 1 || 1;
  const xs = (i: number): number => P + (i / nx) * (W - P * 2);
  const ys = (v: number): number => H - P - ((v - min) / (max - min)) * (H - P * 2);
  const path = (data: ReadonlyArray<EloPoint>): string =>
    data.map((p, i) => `${i ? 'L' : 'M'}${xs(i)},${ys(p.v)}`).join(' ');
  const tickValues = [1700, 1800, 1900, 2000];

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-5">
      <div className="flex justify-between items-baseline mb-1">
        <Caption>{title}</Caption>
        <div className="flex gap-3 font-mono text-[11px]">
          <span>
            <span className="inline-block w-3.5 h-0.5 bg-acid-500 align-middle mr-1" />
            Std
          </span>
          <span>
            <span className="inline-block w-3.5 h-0.5 bg-cyan-500 align-middle mr-1" />
            Rapid
          </span>
        </div>
      </div>
      {empty ? (
        <div className="flex items-center justify-center" style={{ height }}>
          <p className="text-sm text-ink-300 italic max-w-md text-center">
            {emptyHint ?? (
              <>
                Aucun point ELO enregistré. Ajoute un point après chaque
                tournoi (côté admin).
              </>
            )}
          </p>
        </div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ marginTop: 4 }}>
          {/* Gridlines */}
          {tickValues.map((v) => (
            <g key={v}>
              <line
                x1={P}
                y1={ys(v)}
                x2={W - P}
                y2={ys(v)}
                stroke="var(--ink-700)"
                strokeDasharray="2 4"
                opacity="0.5"
              />
              <text
                x={P - 6}
                y={ys(v) + 4}
                fontFamily="JetBrains Mono"
                fontSize="9"
                fill="var(--ink-300)"
                textAnchor="end"
              >
                {v}
              </text>
            </g>
          ))}
          {/* Lines */}
          {standard.length > 1 && (
            <path d={path(standard)} stroke="var(--acid-500)" strokeWidth="2" fill="none" />
          )}
          {rapid.length > 1 && (
            <path d={path(rapid)} stroke="var(--cyan-500)" strokeWidth="2" fill="none" />
          )}
          {/* Endpoint dots */}
          {standard.map((p, i) => (
            <circle
              key={'s-' + i}
              cx={xs(i)}
              cy={ys(p.v)}
              r={i === standard.length - 1 ? 4 : 2.5}
              fill="var(--acid-500)"
            >
              <title>{`FIDE Standard · ${p.label} · ${p.v}`}</title>
            </circle>
          ))}
          {rapid.map((p, i) => (
            <circle
              key={'r-' + i}
              cx={xs(i)}
              cy={ys(p.v)}
              r={i === rapid.length - 1 ? 4 : 2.5}
              fill="var(--cyan-500)"
            >
              <title>{`FIDE Rapid · ${p.label} · ${p.v}`}</title>
            </circle>
          ))}
          {/* Milestones M6 / M12 (cibles PRODUCT.md §3) */}
          {milestones && (
            <g>
              <line
                x1={xs(Math.round(nx * 0.5))}
                y1={P - 4}
                x2={xs(Math.round(nx * 0.5))}
                y2={H - P}
                stroke="var(--violet-500)"
                strokeDasharray="2 4"
                opacity="0.5"
              />
              <text
                x={xs(Math.round(nx * 0.5))}
                y={P - 8}
                textAnchor="middle"
                fontFamily="JetBrains Mono"
                fontSize="10"
                fill="var(--violet-500)"
              >
                M6 · 2000
              </text>
              <line
                x1={xs(nx)}
                y1={P - 4}
                x2={xs(nx)}
                y2={H - P}
                stroke="var(--violet-500)"
                strokeDasharray="2 4"
                opacity="0.5"
              />
              <text
                x={xs(nx) - 4}
                y={P - 8}
                textAnchor="end"
                fontFamily="JetBrains Mono"
                fontSize="10"
                fill="var(--violet-500)"
              >
                M12 · 2200
              </text>
            </g>
          )}
        </svg>
      )}
    </div>
  );
}
